import { Dispatch, SetStateAction, useCallback, useState } from 'react';
import { UPLOAD_MEDIA_TYPE_DETAILS } from '../../../../constants';
import { AITwinCreationStatus, useAITwinSocketEvents, useHandleApiResponse, useToast } from '../../../../hooks';
import { ProspectFooterFields } from '../../../../redux/reducers';
import { useImportIntegrationCallsMutation } from '../../../../services';
import {
  ExternalCallType,
  FilePurpose,
  IntegrationService,
  PracticeProspectDetails,
  ProspectCategory,
  ProspectType,
  UploadMediaType,
} from '../../../../types';
import { AlertType, IntegrationCallsTable, SelectUploadMediaType } from '../../../shared';
import CreateCustomSimulation from '../CreateCustomSimulation';
import ProcessingFailureState from './ProcessingFailureState';
import ProcessingPendingState from './ProcessingPendingState';
import ProcessingSuccessState from './ProcessingSuccessState';
import ShareMediaLink from './ShareMediaLink';
import UploadMediaFile from './UploadMediaFile';

const IMPORT_CALLS_ERROR_MSG = 'Failed to import calls. Please try again later.';

interface CreateAITwinProps {
  setNewCategory: Dispatch<SetStateAction<ProspectCategory | undefined>>;
  setNewType: Dispatch<SetStateAction<ProspectType | undefined>>;
  setMediaType: Dispatch<SetStateAction<UploadMediaType | undefined>>;
  filterProspectCategories: (category: ProspectCategory) => boolean;
  setAiTwinCreationStatus: Dispatch<SetStateAction<AITwinCreationStatus | undefined>>;
  goToPrevStep: () => void;
  newCategory?: ProspectCategory;
  aiTwinCreationStatus?: AITwinCreationStatus;
  newType?: ProspectType;
  mediaType?: UploadMediaType;
}

const CreateAITwin = ({
  filterProspectCategories,
  goToPrevStep,
  setNewCategory,
  setNewType,
  setMediaType,
  setAiTwinCreationStatus,
  aiTwinCreationStatus,
  newCategory,
  newType,
  mediaType,
}: CreateAITwinProps) => {
  const handleApiResponse = useHandleApiResponse();
  const { showToast } = useToast();

  const [importIntegrationCalls, { isLoading: isImportingCalls }] = useImportIntegrationCallsMutation();

  const [aiTwinDetails, setAiTwinDetails] = useState<Omit<PracticeProspectDetails, ProspectFooterFields> | undefined>(
    undefined
  );

  // Listens for AITwin creation events and updates the prospect form.
  useAITwinSocketEvents({ setAiTwinCreationStatus, setAiTwinDetails });

  const onSuccess = useCallback(() => {
    setAiTwinCreationStatus(AITwinCreationStatus.PROCESSING);
  }, [setAiTwinCreationStatus]);

  const handleImportCalls = useCallback(
    async (service: IntegrationService, selectedCallIds: string[], callType?: ExternalCallType) => {
      if (!selectedCallIds.length || !newCategory || !newType) return;

      try {
        const response = await importIntegrationCalls({
          service,
          externalCallIds: selectedCallIds,
          purpose: FilePurpose.PROSPECT_CREATION,
          prospectCategory: newCategory,
          prospectType: newType,
          callType,
        });
        handleApiResponse({ response, errorMsg: IMPORT_CALLS_ERROR_MSG, hideSuccessToast: true, onSuccess });
      } catch (error) {
        console.error(error);
        showToast({ message: IMPORT_CALLS_ERROR_MSG, type: AlertType.ERROR });
      }
    },
    [importIntegrationCalls, handleApiResponse, newCategory, newType, onSuccess, showToast]
  );

  const disabled = !newCategory || !newType;
  const disabledTooltip = disabled ? 'Category and scenario are required' : undefined;

  const sharedMediaProps = {
    onBack: () => setMediaType(undefined),
    newCategory,
    newType,
  };

  if (aiTwinCreationStatus === AITwinCreationStatus.PROCESSING) {
    return <ProcessingPendingState />;
  }

  if (aiTwinCreationStatus === AITwinCreationStatus.FAILED) {
    return <ProcessingFailureState onTryAgain={() => setAiTwinCreationStatus(undefined)} />;
  }

  if (aiTwinCreationStatus === AITwinCreationStatus.COMPLETED && aiTwinDetails) {
    return <ProcessingSuccessState aiTwinDetails={aiTwinDetails} />;
  }

  // If the media type is selected, show the corresponding component.
  if (mediaType) {
    const integrationService = UPLOAD_MEDIA_TYPE_DETAILS[mediaType].service;

    return (
      <>
        {integrationService && (
          <IntegrationCallsTable
            service={integrationService}
            isLoading={isImportingCalls}
            handleImportCalls={handleImportCalls}
            onBack={() => setMediaType(undefined)}
          />
        )}
        {mediaType === UploadMediaType.LINK && <ShareMediaLink {...sharedMediaProps} onSuccess={onSuccess} />}
        {mediaType === UploadMediaType.FILE && <UploadMediaFile {...sharedMediaProps} onSuccess={onSuccess} />}
      </>
    );
  }
  return (
    <div className="flex w-full gap-6">
      <CreateCustomSimulation
        isAITwin
        filterProspectCategories={filterProspectCategories}
        goToPrevStep={goToPrevStep}
        setNewCategory={setNewCategory}
        setNewType={setNewType}
        newCategory={newCategory}
        newType={newType}
        hideNextButton
      />
      <SelectUploadMediaType setMediaType={setMediaType} disabled={disabled} disabledTooltip={disabledTooltip} />
    </div>
  );
};

export default CreateAITwin;
